import React from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import makeStyles from "@mui/styles/makeStyles";
import { getMUIStyle } from "../../Map/LayerLegendPanel/MUIStyle";

const useStyles = makeStyles(getMUIStyle);

/**
 * A customizable tooltip component that wraps Material-UI's Tooltip and IconButton.
 * It provides an info icon by default that shows a tooltip on hover.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Function} [props.onClick] - Callback function triggered when the icon button is clicked
 * @param {string} [props.className] - CSS class name for custom styling
 * @param {string} [props.placement="bottom-end"] - Tooltip placement position ('top', 'bottom', 'left', 'right', etc.)
 * @param {Function} [props.onMouseEnter] - Callback function triggered when mouse enters the tooltip
 * @param {Function} [props.onMouseLeave] - Callback function triggered when mouse leaves the tooltip
 * @param {Object} [props.componentsProps] - Props to be passed to inner components (tooltip, popper, etc.)
 * @param {number} [props.enterDelay=500] - Delay in ms before tooltip appears
 * @param {number} [props.leaveDelay=200] - Delay in ms before tooltip disappears
 * @param {string|node} props.title - Content to be displayed in the tooltip
 * @param {node} [props.icon] - Custom icon element to replace the default InfoIcon
 * @param {string|number} [props.iconSize="15px"] - Size of the icon
 * @param {string} [props.iconColor="black"] - Color of the icon
 *
 * @example
 * // Basic usage
 * <CustomTooltip title="Help text" />
 *
 * @example
 * // Advanced usage with custom icon and styling
 * <CustomTooltip
 *   title={<Typography>Custom tooltip content</Typography>}
 *   icon={<CustomIcon />}
 *   iconSize="20px"
 *   iconColor="primary.main"
 *   placement="top"
 *   componentsProps={{
 *     tooltip: { style: { maxWidth: 200 } }
 *   }}
 * />
 *
 * @returns {React.Component} A tooltip component with an icon button trigger
 */
export default function CustomTooltip(props) {
  const {
    onClick,
    className,
    placement,
    onMouseEnter,
    onMouseLeave,
    componentsProps,
    enterDelay,
    leaveDelay,
    title,
    icon,
    iconSize,
    iconColor,
  } = props;

  const classes = useStyles();
  return (
    <Tooltip
      title={title}
      onClick={onClick}
      className={className ?? classes.tooltip}
      placement={placement ?? "bottom-end"}
      enterDelay={enterDelay ?? 500}
      leaveDelay={leaveDelay ?? 200}
      componentsProps={componentsProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <IconButton
        sx={{
          fontSize: iconSize ?? "15px",
          py: 0,
          pr: 0,
          color: iconColor ?? "black",
        }}
      >
        {icon ?? <InfoIcon fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  );
}
